import React, { JSX } from 'react';

import { Text } from '@videoblocks/design-system';
import { Sparkles } from '@videoblocks/react-icons';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import Modal from './Modal';
import { useGenerateAudio } from './useGenerateAudio';

export interface CreditConfirmationModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    creditCost: number;
    buttonText: string;
    generateRequestBody: () => BodyInit;
    onSuccess?: () => void;
    useStreaming?: boolean;
}

const pluralize = (count: number, singular: string, plural?: string) => {
    if (count === 1) return singular;
    return plural || `${singular}s`;
};

const CreditConfirmationModal = ({
    isOpen,
    setIsOpen,
    creditCost,
    buttonText,
    generateRequestBody,
    onSuccess,
    useStreaming = false,
}: CreditConfirmationModalProps): JSX.Element | null => {
    const { credits } = useVoiceoverContext();
    const { generate, close, isPending, errorMessage } = useGenerateAudio({
        generateRequestBody,
        useStreaming,
        onSuccess,
        onClose: () => setIsOpen(false),
    });

    const remainingCredits = credits - creditCost;
    const hasRemainingCredits = remainingCredits >= 0;

    if (!hasRemainingCredits) {
        return null;
    }

    return (
        <Modal
            title={`Generate this voiceover for ${creditCost} ${pluralize(creditCost, 'credit')}?`}
            confirmDisabled={isPending}
            primaryActionText={
                <div className="flex flex-row gap-x-1">
                    <Sparkles className="w-4 h-4 fill-black" />
                    <span>{isPending ? 'Generating...' : buttonText}</span>
                </div>
            }
            isOpen={isOpen}
            onClose={close}
            onConfirm={generate}
            body={
                <div className="mt-4 flex flex-col gap-4">
                    <Text fontWeight="regular">
                        You&apos;ll have {remainingCredits} {pluralize(remainingCredits, 'credit')} remaining.
                    </Text>

                    {errorMessage && (
                        <Text fontWeight="regular" className="text-red-400">
                            {errorMessage}
                        </Text>
                    )}
                </div>
            }
        />
    );
};

export default CreditConfirmationModal;
